import { Injectable, signal } from '@angular/core';
import {
  MultiValuesFilter,
  Criteria,
  MultiValueCriteriaBucket,
  RangeCriteria,
  MonoValueCriteria,
  MultiValueCriteria,
  Choice,
  ActorCriteriaBucket,
  ActorTypeFilterChoice,
} from '../models/filter-criteria.model';
import {
  SearchQueryActorValuesResource,
  SearchQueryBooleanValueResource,
  SearchQueryRangeValueResource,
  SearchQueryStringValueResource,
  UserPreferencesResourceCriteriaInner,
} from '@shared/openapi/search';
import { FilterType } from '@features/scenarios-browse/enum/filter-criteria.enum';
import { SelectItem, SelectItemGroup } from 'primeng/api';

@Injectable({
  providedIn: 'root',
})
export class FilterBrowseService {
  //Range variables
  rangeDisplayIcons = [{ label: 'Slope', iconBefore: 'car_down', iconAfter: 'car_up' }];
  //MultiValues variables
  multiValueFilter = signal<MultiValuesFilter[]>([]);

  getRangeCriteria(filter: Criteria): RangeCriteria {
    return filter as RangeCriteria;
  }
  getMonovalueCriteria(filter: Criteria): MonoValueCriteria {
    return filter as MonoValueCriteria;
  }
  getMultiValueCriteria(filter: Criteria): MultiValueCriteria {
    return filter as MultiValueCriteria;
  }

  isDisplayIcons(label: string | undefined): boolean {
    return this.rangeDisplayIcons.some((filter) => filter.label === label);
  }

  isTheFilterAGroup(filter: MultiValueCriteria) {
    return filter.choices?.length > 0 && filter.choices[0].type === FilterType.CONTAINER;
  }

  deepEqual(obj1: any, obj2: any): boolean {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  }
  isSameActorCriteria(criteria: ActorCriteriaBucket, actorCriteria: ActorCriteriaBucket): boolean {
    return (
      criteria.label === actorCriteria.label &&
      criteria.name === actorCriteria.name &&
      criteria.type === actorCriteria.type &&
      this.deepEqual(criteria.actor?.actorType, actorCriteria.actor?.actorType) &&
      this.deepEqual(criteria.actor?.actorRole, actorCriteria.actor?.actorRole)
    );
  }

  initChoices(filter: MultiValueCriteria, isGroup: boolean): SelectItemGroup[] | SelectItem<Choice>[] {
    let choices: SelectItemGroup[] | SelectItem<Choice>[];
    if (isGroup) {
      choices = this.choicesToItemSelectGroupMap(filter.choices);
    } else {
      choices = filter.choices.map((choice) => ({ label: choice.label, value: choice }));
    }
    return choices;
  }

  choicesToItemSelectGroupMap(choices: Choice[]) {
    // Convert choices into SelectItemGroup[] to enable group option functionality
    return choices.map((container) => {
      return {
        label: container.label,
        value: container.name,
        items: (container.children || []).map((choice) => {
          return {
            label: choice.label,
            value: choice,
          };
        }),
      };
    }) as SelectItemGroup[];
  }

  choicesToTreeNodeFormat(choices: Choice[]) {
    return choices.map((container, i) => ({
      key: `parent-${i}`,
      label: container.label,
      value: container.name,
      selectable: false,
      children: (container.children || []).map((choice, j) => ({
        key: `parent-${i}-child-${j}`,
        label: choice.label,
        value: choice,
      })),
    })) as ActorTypeFilterChoice[];
  }

  getValuesFromCriteria(criteria: Criteria): string[] {
    if (!criteria) {
      return [];
    }

    switch (criteria.type) {
      case FilterType.MULTIVALUED: {
        const tickedChoices = (criteria as MultiValueCriteriaBucket)?.tickedChoices ?? [];

        // Handle choices with parameterAssignment (behavior parameters case)
        return tickedChoices.map((choice) => {
          if (choice.parameterAssignment) {
            const assignment = choice.parameterAssignment;
            const parentPrefix = (choice as any).parentName ? `Behavior ${(choice as any).parentName} - ` : '';

            if (assignment.mode === 'range') {
              const hasMin = assignment.rangeMin !== undefined && assignment.rangeMin !== null;
              const hasMax = assignment.rangeMax !== undefined && assignment.rangeMax !== null;

              // Case: both min and max are defined
              if (hasMin && hasMax) {
                const minSymbol = assignment.isMinExclusive ? ']' : '[';
                const maxSymbol = assignment.isMaxExclusive ? '[' : ']';
                return `${parentPrefix}${choice.label}: ${minSymbol}${assignment.rangeMin}, ${assignment.rangeMax}${maxSymbol}`;
              }
              // Case: only min is defined (e.g., "4<" or "4≤")
              else if (hasMin && !hasMax) {
                const symbol = assignment.isMinExclusive ? '<' : '≤';
                return `${parentPrefix}${choice.label}: ${assignment.rangeMin}${symbol}`;
              }
              // Case: only max is defined (e.g., "<4" or "≤4")
              else if (!hasMin && hasMax) {
                const symbol = assignment.isMaxExclusive ? '<' : '≤';
                return `${parentPrefix}${choice.label}: ${symbol}${assignment.rangeMax}`;
              }
            } else if (
              assignment.mode === 'value' &&
              assignment.selectedValues &&
              assignment.selectedValues.length > 0
            ) {
              // Find the labels of the selected values
              const selectedLabels = assignment.selectedValues
                .map((valueName) => {
                  const selectedChild = choice.children?.find((c) => c.name === valueName);
                  return selectedChild?.label ?? valueName;
                })
                .join(', ');
              return `${parentPrefix}${choice.label}: ${selectedLabels}`;
            }
          }
          return choice.label;
        });
      }
      case FilterType.VALUE: {
        const stringCriteria = criteria as SearchQueryStringValueResource;
        return [stringCriteria.value!];
      }
      case FilterType.RANGE: {
        const rangeCriteria = criteria as RangeCriteria;
        return [rangeCriteria.label + ': [' + rangeCriteria.min + ',' + rangeCriteria.max + ']'];
      }
      case FilterType.BOOLEAN: {
        const booleanCriteria = criteria as SearchQueryBooleanValueResource;
        return booleanCriteria.enabled ? [criteria.name!] : ['NO ' + criteria.name];
      }
      case FilterType.MONOVALUED: {
        const monoValueCriteria = criteria as MonoValueCriteria;
        return [monoValueCriteria.value ?? ''];
      }
      case FilterType.ACTOR: {
        const actorCriteria = criteria as ActorCriteriaBucket;
        if (actorCriteria.noActor) {
          return ['No Actor'];
        } else {
          const actor = actorCriteria.actor;
          const actorElements = [];
          if (actor!.actorType) {
            actorElements.push(actor!.actorType.label);
          }
          if (actor!.actorRole) {
            actorElements.push(actor!.actorRole.label);
          }
          return actorElements;
        }
      }
      case FilterType.CONTAINER: {
        let value: string[] = [];
        criteria.children?.forEach((children) => {
          value = value.concat(this.getValuesFromCriteria(children));
        });
        return value;
      }
      default: {
        return [];
      }
    }
  }

  mapBucketToSearch(criteriaBucket: Criteria[]): UserPreferencesResourceCriteriaInner[] {
    const criteriaBucketWithoutActorOrContainer = this.filterCriteriaByType(
      criteriaBucket,
      [FilterType.ACTOR, FilterType.CONTAINER],
      false,
    );
    const criteriaBucketWithActor = this.filterCriteriaByType(criteriaBucket, [FilterType.ACTOR]);
    const criteriaBucketWithContainer = this.filterCriteriaByType(criteriaBucket, [FilterType.CONTAINER]);
    const criteriaBucketMergedActors = this.mapActorBucketToSearch(criteriaBucketWithActor);
    const criteriaBucketMergedContainer = this.mapContainerBucketToSearch(criteriaBucketWithContainer);
    criteriaBucketWithoutActorOrContainer.push(...criteriaBucketMergedContainer);
    return this.mergeMappedCriteriaWithActors(
      this.mapCriteriaList(criteriaBucketWithoutActorOrContainer),
      criteriaBucketMergedActors,
    );
  }
  mapCriteriaList(criteriaList: Criteria[]): UserPreferencesResourceCriteriaInner[] {
    return criteriaList.map((criteria) => this.mapCriteriaToSearch(criteria));
  }

  mergeMappedCriteriaWithActors(
    mappedCriteria: UserPreferencesResourceCriteriaInner[],
    mergedActors: SearchQueryActorValuesResource,
  ): UserPreferencesResourceCriteriaInner[] {
    if (Object.keys(mergedActors).length > 0) {
      mappedCriteria.push(mergedActors as UserPreferencesResourceCriteriaInner);
    }
    return mappedCriteria;
  }
  filterCriteriaByType(criteriaBucket: Criteria[], types: FilterType[], include = true): Criteria[] {
    return criteriaBucket.filter((criteria) =>
      include ? types.includes(criteria.type as FilterType) : !types.includes(criteria.type as FilterType),
    );
  }

  mapActorBucketToSearch(actorBucket: Criteria[]): SearchQueryActorValuesResource {
    if (actorBucket.length === 0) return {};
    return {
      name: 'ACTOR',
      noActor: (actorBucket[0] as ActorCriteriaBucket)!.noActor,
      actors: actorBucket.map((criteria) => {
        const actorType = (criteria as ActorCriteriaBucket).actor?.actorType?.name;
        const actorRole = (criteria as ActorCriteriaBucket).actor?.actorRole?.name;
        const searchOn = (criteria as ActorCriteriaBucket).actor?.actorType?.type;

        return {
          ...(actorType && { actorType }),
          ...(actorRole && { actorRole }),
          ...(searchOn && { searchOn }),
        };
      }),
    };
  }

  mapContainerBucketToSearch(containerBucket: Criteria[]): Criteria[] {
    if (containerBucket.length === 0) return [];
    return containerBucket
      .map((criteria) => criteria.children)
      .filter((child): child is Criteria[] => child !== undefined)
      .flat();
  }

  mapCriteriaToSearch(criteria: Criteria): UserPreferencesResourceCriteriaInner {
    switch (criteria.type) {
      case FilterType.MULTIVALUED: {
        if (criteria.name === 'INFRASTRUCTURE' || criteria.name === 'INFRASTRUCTURE_ELEMENT') {
          const ticked = (criteria as MultiValueCriteriaBucket)?.tickedChoices ?? [];
          const valuesMap: Record<string, number> = {};
          ticked.forEach((choice) => {
            const level = choice.level ?? 1;
            if (valuesMap[choice.name] === undefined || valuesMap[choice.name] < level) {
              valuesMap[choice.name] = level;
            }
          });
          return { name: criteria.name, treeValues: valuesMap } as UserPreferencesResourceCriteriaInner;
        } else {
          const ticked = (criteria as MultiValueCriteriaBucket)?.tickedChoices ?? [];

          // Check if we have choices with parameterAssignment (BEHAVIOR case)
          const hasParameterAssignment = ticked.some((choice) => choice.parameterAssignment);

          if (hasParameterAssignment) {
            // For behaviors with parameters, send an enriched structure as objects
            // The serialization framework will handle the JSON conversion
            const behaviorValues = ticked.map((choice) => {
              const choiceData: any = {
                name: choice.name,
                level: choice.level ?? 1,
              };

              // Add parentName if it exists
              if ((choice as any).parentName) {
                choiceData.parentName = (choice as any).parentName;
              }

              if (choice.parameterAssignment) {
                const assignment = choice.parameterAssignment;

                if (assignment.mode === 'range') {
                  // Use a structure similar to SearchQueryRangeValueResource
                  choiceData.range = {
                    ...(assignment.rangeMin !== undefined &&
                      assignment.rangeMin !== null && { min: assignment.rangeMin }),
                    ...(assignment.rangeMax !== undefined &&
                      assignment.rangeMax !== null && { max: assignment.rangeMax }),
                    ...(assignment.isMinExclusive !== undefined && { minExclusive: assignment.isMinExclusive }),
                    ...(assignment.isMaxExclusive !== undefined && { maxExclusive: assignment.isMaxExclusive }),
                  };
                } else if (
                  assignment.mode === 'value' &&
                  assignment.selectedValues &&
                  assignment.selectedValues.length > 0
                ) {
                  // Send array of selected values
                  choiceData.abstractValues = assignment.selectedValues;
                }
              }
              return choiceData;
            });

            // Cast to any to bypass TypeScript's strict typing since we're extending the API
            return { name: criteria.name, behaviorValues: behaviorValues } as any;
          }
          if (criteria.name === 'BEHAVIOR') {
            return {
              name: criteria.name,
              behaviorValues: ticked.map((choice) => ({
                name: choice.name,
                level: choice.level ?? 1,
              })),
            } as any;
          }
          return {
            name: criteria.name,
            values: ticked.map((choice) => choice.name),
          };
        }
      }
      case FilterType.MONOVALUED:
      case FilterType.VALUE: {
        const stringCriteria = criteria as SearchQueryStringValueResource;
        return {
          name: stringCriteria.name,
          value: stringCriteria.value,
        };
      }
      case FilterType.RANGE: {
        const rangeCriteria = criteria as SearchQueryRangeValueResource;
        return {
          name: rangeCriteria.name,
          min: rangeCriteria.min,
          max: rangeCriteria.max,
        };
      }
      case FilterType.BOOLEAN: {
        const booleanCriteria = criteria as SearchQueryBooleanValueResource;
        return {
          name: criteria.name,
          enabled: booleanCriteria.enabled,
        };
      }
      default: {
        return { name: criteria.name };
      }
    }
  }

  updateCriteriaWithinCriteriaList(criteriaList: Criteria[], newCriteria: Criteria): Criteria[] {
    let existingIndex: number;
    if (criteriaList.length === 0) {
      existingIndex = -1;
    } else {
      existingIndex = criteriaList.findIndex((criteria: Criteria) => criteria.name === newCriteria.name);
    }
    if (existingIndex !== -1 && newCriteria.type !== FilterType.ACTOR) {
      // UPDATE : If the criteria already exists, update its values
      const updatedCriteria = { ...criteriaList[existingIndex], ...newCriteria };
      criteriaList.splice(existingIndex, 1, updatedCriteria);
      // DELETE : Ensures that criteria with no values are not kept in the criteriaBucket
      if (
        (updatedCriteria.type === FilterType.VALUE && (updatedCriteria as MonoValueCriteria)?.value === '') ||
        (updatedCriteria.type === FilterType.MULTIVALUED &&
          (updatedCriteria as MultiValueCriteriaBucket)?.tickedChoices?.length === 0) ||
        (updatedCriteria.type === FilterType.RANGE &&
          (updatedCriteria as RangeCriteria)?.min === (criteriaList[existingIndex] as RangeCriteria).minValue &&
          (updatedCriteria as RangeCriteria)?.max === (criteriaList[existingIndex] as RangeCriteria).maxValue)
      ) {
        criteriaList.splice(existingIndex, 1);
      }
      // CREATE : If the criteria does not exist, add it
    } else {
      criteriaList.push(newCriteria);
    }
    return criteriaList;
  }
}
